// Story 5.6 — Shared "Listing live" toast with a 5-second Undo affordance.
//
// Cashes in deferred work explicitly scoped out of Stories 5.2 + 5.3:
// the success-toast on a just-created listing now exposes an "Undo"
// action button per UX-DR8 + UX-spec L1314 + L1770. Clicking Undo
// POSTs to the matching cancel endpoint with the returned listing_id;
// after 5 seconds OR after dismissal the toast auto-closes; the
// listing stays active if not undone.
//
// Pure DOM-string toast rendered via react-hot-toast's t.id close
// hook. The cancel POST mirrors the useCancelShareListing /
// useCancelSealedDossierListing hook bodies (kept inline here to
// avoid pulling React-state plumbing into a non-component module).

import toast from "react-hot-toast";
import type { QueryClient } from "@tanstack/react-query";
import React from "react";

import { attachRequestIdToScope } from "../../lib/api-client";

const UNDO_DURATION_MS = 5_000;

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface ShowUndoListingToastArgs {
  /** Asset surface — determines which cancel endpoint is called. */
  kind: "share" | "sealed_dossier";
  /** The just-created listing's id; the Undo POST targets this. */
  listingId: string;
  /** Cache keys to invalidate on undo success. */
  invalidationKeys: ReadonlyArray<ReadonlyArray<unknown>>;
  /** TanStack QueryClient — passed in so the helper can invalidate caches. */
  queryClient: QueryClient;
}

async function postCancelInline(
  kind: "share" | "sealed_dossier",
  listingId: string,
): Promise<void> {
  const url =
    kind === "share"
      ? `/api/v1/marketplace/listings/share/${listingId}/cancel`
      : `/api/v1/marketplace/listings/sealed-dossier/${listingId}/cancel`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: "{}",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Undo failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) {
        message = raw.error.message;
      }
    } catch {
      // body not JSON; keep default
    }
    throw new Error(message);
  }
}

export function showUndoListingToast(args: ShowUndoListingToastArgs): void {
  const { kind, listingId, invalidationKeys, queryClient } = args;

  toast(
    (t) =>
      React.createElement(
        "div",
        {
          className: "flex items-center gap-3",
          "data-testid": "undo-listing-toast",
        },
        React.createElement("span", null, "Listing live"),
        React.createElement(
          "button",
          {
            type: "button",
            "data-testid": "undo-listing-button",
            className: "text-sm underline underline-offset-2",
            onClick: async () => {
              toast.dismiss(t.id);
              try {
                await postCancelInline(kind, listingId);
                toast.success("Listing withdrawn");
                for (const key of invalidationKeys) {
                  void queryClient.invalidateQueries({
                    queryKey: key as readonly unknown[],
                  });
                }
              } catch (err) {
                const message =
                  err instanceof Error ? err.message : "Undo failed.";
                toast.error(message);
                // Best-effort cache reconciliation so the UI catches up.
                for (const key of invalidationKeys) {
                  void queryClient.invalidateQueries({
                    queryKey: key as readonly unknown[],
                  });
                }
              }
            },
          },
          "Undo",
        ),
      ),
    {
      duration: UNDO_DURATION_MS,
    },
  );
}
